/* eslint-env browser */
import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
  type ReactNode
} from 'react';
import { type Socket, io } from 'socket.io-client';
import { useStore } from 'react-redux';
import { useAppDispatch, useAppDispatchThunk, useAppSelector } from './hooks';
import type { RootState } from './store';
import {
  selectSocketAddress,
  changeSocketConnected,
  changeConnectionError,
  SECURE_CONNECTION_FAILED
} from '../redux/settingsSlice';
import {
  selectJudge,
  updateJudgesList,
  selectSocketsCache,
  clearSocketsCache,
  updateTask,
  updateTaskManagerQueue,
  updateTaskResults,
  updateParticipantResults,
  clearParticipantResultsForRound,
  removeTaskCompletedByRound,
  type SocketElement,
  type JudgesListSettings,
  type ManagerItem,
  type Dance
} from '../redux/workSlice';

import {
  addTimetableDetails,
  setActiveCompetition,
  setTimetable,
  addMonitorDetails,
  clearMonitorDetails,
  type TimetableDetails,
  type TimetableItem,
  type Competition
} from '../redux/timetableSlice';
import {
  setVisualState,
  applyVisualControl,
  type VisualState,
  type VisualControlCommand
} from '../redux/visualSlice';
import {
  setJudgesStatus,
  type JudgesStatusPayload
} from '../redux/judgesStatusSlice';
import {
  setTimerState,
  type TimerStatePayload
} from '../redux/timerSlice';
import { setPublicResults } from '../redux/publicResultsSlice';
import { markLoginAnnounced } from '../helpers/announcedLogin';
import { startDeviceHeartbeat } from '../helpers/deviceHeartbeat';
import {
  setWarnings,
  receiveNewWarning,
  type EnrichedWarning
} from '../redux/warningsSlice';
import {
  setBreakingBracket,
  BreakingBracketView,
} from '../redux/breakingBracketSlice';
import {
  selectDeviceUuid,
  selectDeviceId,
  selectDeviceType,
  changeSelectedHall
} from '../redux/settingsSlice';
import SOCKET from '../constants/socketTypes';
import DEVICE, { isJudgeDeviceType } from '../constants/deviceType';

export const SocketContext = createContext<Socket | null>(null);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [isConnected, setConnected] = useState(false);
  const [isSocketInitialized, setSocketInitialized] = useState(false);

  const socket = useRef<Socket | null>(null);
  const dispatch = useAppDispatch();
  const dispatchThunk = useAppDispatchThunk();
  const store = useStore<RootState>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOnMessage = (_message?: unknown): void => {
    // store.dispatch here
  };
  useEffect(() => {
    dispatch(changeSocketConnected(isConnected));
  }, [isConnected]);

  const socketUrl = useAppSelector(selectSocketAddress);
  const socketsCache = useAppSelector(selectSocketsCache);
  const socketsCacheRef = useRef(socketsCache);
  const chosenJudge = useAppSelector(selectJudge);
  const chosenJudgeRef = useRef(chosenJudge);
  const uuid = useAppSelector(selectDeviceUuid);
  const deviceId = useAppSelector(selectDeviceId);
  // Same reason as chosenJudgeRef: the socket effect closes over these once, so
  // a device numbered (or re-numbered) in Setup after the first connect would
  // otherwise keep announcing its old identity on every reconnect.
  const deviceIdentityRef = useRef({ uuid, deviceId });
  const deviceType = useAppSelector(selectDeviceType);
  const deviceTypeRef = useRef(deviceType);
  const activeCompetition = useAppSelector(
    (state) => state.timetable?.activeCompetition
  );
  const prevCompetitionUuidRef = useRef<string | undefined>(activeCompetition?.uuid);

  useEffect(() => {
    chosenJudgeRef.current = chosenJudge;
  }, [chosenJudge]);

  useEffect(() => {
    deviceIdentityRef.current = { uuid, deviceId };
  }, [uuid, deviceId]);

  useEffect(() => {
    deviceTypeRef.current = deviceType;
  }, [deviceType]);

  // The socket effect below runs once per socketUrl, so its 'connect' handler
  // would otherwise close over the cache as it was on first render and replay
  // that same stale array on every reconnect, forever.
  useEffect(() => {
    socketsCacheRef.current = socketsCache;
  }, [socketsCache]);

  // Reset hall selection when competition changes
  useEffect(() => {
    const currentUuid = activeCompetition?.uuid;
    if (prevCompetitionUuidRef.current && currentUuid && currentUuid !== prevCompetitionUuidRef.current) {
      dispatch(changeSelectedHall('all'));
    }
    prevCompetitionUuidRef.current = currentUuid;
  }, [activeCompetition?.uuid]);

  useEffect(() => {
    let reconnectInterval: ReturnType<typeof setInterval> | undefined;
    let stopHeartbeat: (() => void) | undefined;

    try {
      if (!isConnected && !isSocketInitialized) {
        socket.current = io(socketUrl, {
          transports: ['websocket', 'webtransport']
        });

        socket.current?.on('connect', () => {
          try {
            setSocketInitialized(true); // Mark socket as initialized
            dispatch(changeConnectionError(''));

            // Must read the ref, never the closure: this effect runs once per
            // socketUrl, so a closed-over chosenJudge is frozen at whoever was
            // signed in when the socket was created. Every reconnect would then
            // re-announce that judge - which is a login for a stale adjudicator
            // on this device, and the desktop reads it as a judge swap.
            const judge = chosenJudgeRef.current;
            // Only a judge's phone announces a judge: one left in the store of
            // a phone now used as MC, monitor or for training would otherwise
            // be logged in on every reconnect.
            if (
              judge?.uuid &&
              judge?.letter &&
              judge?.name &&
              isJudgeDeviceType(deviceTypeRef.current)
            ) {
              const adjudicatorSocket: SocketElement = {
                action: SOCKET.ADJUDICATOR,
                args: [
                  {
                    type: SOCKET.ADJUDICATOR_LOG_IN,
                    letter: judge.letter,
                    name: judge.name,
                    adjudicatorUuid: judge.uuid,
                    socketId: socket.current?.id,
                    deviceUuid: deviceIdentityRef.current.uuid,
                    deviceId: deviceIdentityRef.current.deviceId,
                    // A re-announce, not a pick: marX never lets it take the
                    // judge from another phone that has them.
                    auto: true
                  }
                ],
                acknowledge: false,
                volatile: false
              };
              socket.current?.emit(
                adjudicatorSocket.action,
                ...adjudicatorSocket.args
              );
              markLoginAnnounced(socket.current?.id, judge.uuid);
            }

            // Flush anything that could not be sent while the socket was
            // down. This is a pending queue, not a log: it is drained below,
            // otherwise every element would be re-sent on every reconnect for
            // the life of the install.
            const pending = socketsCacheRef.current;
            pending.forEach((s: SocketElement) => {
              try {
                // Adjudicator login/logout is never replayed. The current login
                // was already emitted above from chosenJudge; anything of this
                // kind still in the queue predates the guard in addSocketToCache
                // and is a stale snapshot of a previous judge, which the desktop
                // would read as a judge swap on this device and act on.
                if (s.action === SOCKET.ADJUDICATOR) return;
                if (s.volatile) {
                  socket.current?.volatile?.emit(s.action, ...s.args);
                } else {
                  socket.current?.emit(s.action, ...s.args);
                }
              } catch (err) {
                console.error(
                  'Error emitting socket action:',
                  (err as Error).message
                );
              }
            });
            if (pending.length) {
              dispatch(clearSocketsCache());
            }

            setConnected(true);
          } catch (err) {
            console.error(
              'Error during socket connection setup:',
              (err as Error).message
            );
          }
        });

        socket.current?.on('disconnect', (reason) => {
          if (socket.current?.active) {
            console.log(
              'Socket disconnected temporarily, attempting to reconnect...'
            );
            setConnected(false);
            // temporary disconnection, the socket will automatically try to reconnect
          } else {
            // the connection was forcefully closed by the server or the client itself
            // in that case, `socket.connect()` must be manually called in order to reconnect
            try {
              setConnected(false);
              setSocketInitialized(false); // Reset initialization state on disconnect
            } catch (err) {
              console.error(
                'Error during socket disconnect handling:',
                (err as Error).message
              );
            }
            console.log(reason);
            socket.current?.connect();
          }
        });
        // socket.current?.onAny((event, ...args) => {
        //   // Handle any event that is not explicitly handled
        //   console.log(`Socket event: ${event}`, args);
// console.log(deviceTypeRef.current);
        // });
        socket.current?.on(SOCKET.MESSAGE, (message) => {
          try {
            handleOnMessage(message);
          } catch (err) {
            console.error(
              'Error handling SOCKET.MESSAGE:',
              (err as Error).message
            );
          }
        });

        socket.current?.on(
          SOCKET.TIMETABLE_DETAILS,
          (arg: TimetableDetails) => {
            try {
              if (
                deviceTypeRef.current === DEVICE.MC ||
                deviceTypeRef.current === DEVICE.CHAIRPERSON ||
                deviceTypeRef.current === DEVICE.DJ ||
                deviceTypeRef.current === DEVICE.FLOOR_MANAGER ||
                deviceTypeRef.current === DEVICE.LED
              ) {
                dispatch(addTimetableDetails(arg));
              }
            } catch (err) {
              console.error(
                'Error handling SOCKET.TIMETABLE_DETAILS:',
                (err as Error).message
              );
            }
          }
        );
        socket.current?.on(SOCKET.RESET_MONITOR, () => {
          try {
            dispatch(clearMonitorDetails());
          } catch (err) {
            console.error(
              'Error handling SOCKET.RESET_MONITOR:',
              (err as Error).message
            );
          }
        });
        socket.current?.on(SOCKET.MONITOR_DETAILS, (arg: TimetableDetails) => {
          try {
            if (deviceTypeRef.current === DEVICE.MONITOR) {
              dispatch(addMonitorDetails(arg));
            }
          } catch (err) {
            console.error(
              'Error handling SOCKET.MONITOR_DETAILS:',
              (err as Error).message
            );
          }
        });
        socket.current?.on(
          SOCKET.JUDGES_LIST,
          (arg: object, settings: JudgesListSettings) => {
            // console.log('Judges List:', arg, settings);
            try {
              dispatch(
                updateJudgesList({ list: arg, settings: settings || {} })
              );
            } catch (err) {
              console.error(
                'Error handling SOCKET.JUDGES_LIST:',
                (err as Error).message
              );
            }
          }
        );

        socket.current?.on(SOCKET.TIMETABLE, (arg: TimetableItem[]) => {
          try {
            dispatch(setTimetable(arg));
          } catch (err) {
            console.error(
              'Error handling SOCKET.TIMETABLE:',
              (err as Error).message
            );
          }
        });
        socket.current?.on(SOCKET.COMPETITION, (arg: Competition) => {
          try {
            dispatch(setActiveCompetition(arg));
          } catch (err) {
            console.error(
              'Error handling SOCKET.COMPETITION:',
              (err as Error).message
            );
          }
        });

        // Visual Control Events (LED/Broadcast)
        socket.current?.on(SOCKET.VISUAL_STATE, (state: VisualState) => {
          try {
            dispatch(setVisualState(state));
          } catch (err) {
            console.error(
              'Error handling SOCKET.VISUAL_STATE:',
              (err as Error).message
            );
          }
        });

        socket.current?.on(
          SOCKET.VISUAL_CONTROL,
          (command: VisualControlCommand) => {
            try {
              dispatch(applyVisualControl(command));
            } catch (err) {
              console.error(
                'Error handling SOCKET.VISUAL_CONTROL:',
                (err as Error).message
              );
            }
          }
        );

        // Countdown timer state - store with local receive time so displays
        // can derive a local deadline and count down without further traffic
        socket.current?.on(SOCKET.TIMER_STATE, (state: TimerStatePayload) => {
          try {
            dispatch(setTimerState({ ...state, receivedAt: Date.now() }));
          } catch (err) {
            console.error(
              'Error handling SOCKET.TIMER_STATE:',
              (err as Error).message
            );
          }
        });

        // Public results address for the monitor QR code. Sent as a const emit
        // by the desktop, so it is replayed on every (re)connect and on
        // RESEND_CONST_EMIT - no subscription needed on this side.
        socket.current?.on(
          SOCKET.PUBLIC_RESULTS_URL,
          (payload: { url?: string; label?: string }) => {
            try {
              dispatch(setPublicResults(payload || {}));
            } catch (err) {
              console.error(
                'Error handling SOCKET.PUBLIC_RESULTS_URL:',
                (err as Error).message
              );
            }
          }
        );

        // Judges status updates (for chairperson and MC)
        socket.current?.on(
          SOCKET.JUDGES_STATUS,
          (payload: JudgesStatusPayload) => {
            try {
              if (
                deviceTypeRef.current === DEVICE.CHAIRPERSON ||
                deviceTypeRef.current === DEVICE.MC
              ) {
                //  console.log('Received JUDGES_STATUS update:', payload);
                dispatch(setJudgesStatus(payload));
              }
            } catch (err) {
              console.error(
                'Error handling SOCKET.JUDGES_STATUS:',
                (err as Error).message
              );
            }
          }
        );

        // Breaking bracket. Unlike timetable/monitor details this one feed
        // serves every screen that shows the draw - the monitor, the MC, the
        // chairperson and the LED wall - so it is not gated by device type.
        socket.current?.on(
          SOCKET.BREAKING_BRACKET,
          (payload: BreakingBracketView) => {
            try {
              if (payload?.categoryUuid) {
                dispatch(setBreakingBracket(payload));
              }
            } catch (err) {
              console.error(
                'Error handling SOCKET.BREAKING_BRACKET:',
                (err as Error).message
              );
            }
          }
        );

        // Chairperson warnings — full snapshot (replaces the stored list)
        socket.current?.on(
          SOCKET.CHAIRPERSON_WARNINGS,
          (payload: { warnings: EnrichedWarning[] }) => {
            try {
              if (deviceTypeRef.current === DEVICE.CHAIRPERSON) {
                dispatch(setWarnings(payload?.warnings ?? []));
              }
            } catch (err) {
              console.error(
                'Error handling SOCKET.CHAIRPERSON_WARNINGS:',
                (err as Error).message
              );
            }
          }
        );

        // Chairperson warnings — a single new warning (stores it and, when it
        // belongs to this hall, queues the modal alert)
        socket.current?.on(
          SOCKET.CHAIRPERSON_NEW_WARNING,
          (payload: { warning: EnrichedWarning }) => {
            try {
              if (
                deviceTypeRef.current === DEVICE.CHAIRPERSON &&
                payload?.warning
              ) {
                dispatchThunk(receiveNewWarning(payload.warning));
              }
            } catch (err) {
              console.error(
                'Error handling SOCKET.CHAIRPERSON_NEW_WARNING:',
                (err as Error).message
              );
            }
          }
        );

        // Buffer to prevent duplicate task handling
        const taskBuffer = new Map<string, number>();
        const TASK_BUFFER_TIME = 2000; // 2 seconds

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        socket.current?.on(SOCKET.ADJUDICATOR_SHEET, (task: any) => {
          try {
            if (!task) {
              throw new Error('Invalid task data received');
            }
            // Check buffer for duplicate task
            const now = Date.now();
            const lastReceived = taskBuffer.get(task.uuid);
            if (lastReceived && now - lastReceived < TASK_BUFFER_TIME) {
              // Ignore duplicate within buffer time
              return;
            }
            taskBuffer.set(task.uuid, now);
            // Clean up old entries
            for (const [uuid, timestamp] of taskBuffer) {
              if (now - timestamp > TASK_BUFFER_TIME) {
                taskBuffer.delete(uuid);
              }
            }
            if (chosenJudgeRef.current?.uuid === task.adjudicatorUuid) {
              // A sheet whose marks the desktop restored from an earlier
              // version (SOS → Restore results) lists the restored dances:
              // their marks replace what this phone holds for them rather than
              // merging with marks re-entered since. The list comes with this
              // push only and is not stored with the task.
              const { replaceDanceUuids, ...sheet } = task;

              // If the task is being re-sent for revision (status is not done),
              // clear any previous completion record so the home screen no
              // longer treats it as finished. Scope by ttItemUuid so a split
              // round's other style keeps its completion record.
              if (sheet.taskResults?.status !== 'done') {
                dispatch(
                  removeTaskCompletedByRound({
                    roundUuid: sheet.roundUuid,
                    adjudicatorUuid: sheet.adjudicatorUuid,
                    ttItemUuid: sheet.ttItemUuid
                  })
                );
              }

              dispatch(updateTask(sheet));
              dispatch(updateTaskResults(sheet.taskResults));
              if (sheet.taskResults.participantResults.length === 0) {
                dispatch(clearParticipantResultsForRound(sheet));
              } else {
                if (
                  Array.isArray(replaceDanceUuids) &&
                  replaceDanceUuids.length > 0
                ) {
                  dispatch(
                    clearParticipantResultsForRound({
                      ...sheet,
                      dances: sheet.dances.filter((d: Dance) =>
                        replaceDanceUuids.includes(d.uuid)
                      )
                    })
                  );
                }
                dispatch(
                  updateParticipantResults(sheet.taskResults.participantResults)
                );
              }
              socket.current?.emit(SOCKET.ACK_ADJUDICATOR_SHEET, {
                type: SOCKET.ACK_ADJUDICATOR_SHEET,
                uuid: sheet.uuid,
                adjudicatorUuid: chosenJudgeRef.current?.uuid,
                roundUuid: sheet.roundUuid
              });
            }
          } catch (err) {
            console.error(
              'Error handling SOCKET.ADJUDICATOR_SHEET:',
              (err as Error).message
            );
          }
        });

        socket.current?.on(
          SOCKET.TASK_MANAGET_QUEUE,
          (queue: ManagerItem[]) => {
            try {
              if (!queue) {
                throw new Error('Invalid queue data received');
              }
              // console.log('Task Manager Queue:', queue);
              dispatch(updateTaskManagerQueue(queue));
            } catch (err) {
              console.error(
                'Error handling SOCKET.TASK_MANAGET_QUEUE:',
                (err as Error).message
              );
            }
          }
        );
        socket.current?.on('connect_error', (error: Error) => {
          const secure =
            socketUrl.startsWith('https') || socketUrl.startsWith('wss');
          dispatch(
            changeConnectionError(
              secure ? SECURE_CONNECTION_FAILED : error.message
            )
          );
          if (socket.current?.active) {
            // temporary failure, the socket will automatically try to reconnect
          } else {
            // the connection was denied by the server
            // in that case, `socket.connect()` must be manually called in order to reconnect
            console.log(error.message);
            try {
              socket.current?.connect();
            } catch (err) {
              console.error(
                'Error during socket reconnection:',
                (err as Error).message
              );
            }
          }
        });

        socket.current?.on(
          'error',
          function transmit_server_connection_handshake_error() {
            return null;
          }
        );

        if (socket.current) {
          stopHeartbeat = startDeviceHeartbeat(socket.current, store.getState);
        }
      }
    } catch (err) {
      console.error(
        'Error during socket initialization:',
        (err as Error).message
      );
    }

    return () => {
      stopHeartbeat?.();
      if (socket.current) {
        try {
          socket.current.off('connect');
          socket.current.off('disconnect');
          socket.current.off(SOCKET.MESSAGE);
          socket.current.off(SOCKET.TIMETABLE_DETAILS);
          socket.current.off(SOCKET.JUDGES_LIST);
          socket.current.off(SOCKET.TIMETABLE);
          socket.current.off(SOCKET.ADJUDICATOR_SHEET);
          socket.current.off(SOCKET.TASK_MANAGET_QUEUE);
          socket.current.off(SOCKET.VISUAL_STATE);
          socket.current.off(SOCKET.VISUAL_CONTROL);
          socket.current.off(SOCKET.TIMER_STATE);
          socket.current.off(SOCKET.PUBLIC_RESULTS_URL);
          socket.current.off('error');
        } catch (err) {
          console.error('Error during socket cleanup:', (err as Error).message);
        }

        if (socket.current.connected) {
          socket.current.disconnect();
        }
      }

      if (reconnectInterval) {
        clearInterval(reconnectInterval);
      }
    };
  }, [socketUrl]);

  return (
    <SocketContext.Provider value={socket.current}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): Socket | null => useContext(SocketContext);
